<template>
  <ClientOnly>
    <div
      class="atoms-comparison-table pt-[2.8125rem] pb-10 md:py-20 px-4 lg:px-0"
      :class="bgClass"
    >
      <div class="max-w-wrapper-sm mx-auto" :class="[wrapperClass]">
        <!-- eslint-disable vue/no-v-html -->
        <p
          v-if="title"
          class="text-center text-3xl/[120%] md:text-4xl/[120%] text-semantic-surface-on-surface font-semibold"
          :class="{
            'mb-6 md:mb-10': !description,
            'mb-2 md:mb-4': description,
          }"
          v-html="title"
        />

        <p
          v-if="description"
          class="text-center text-sm/[150%] md:text-lg/[150%] mb-6 md:mb-10 text-semantic-surface-on-surface"
          v-html="description"
        />

        <div
          class="w-full"
          :class="{ 'overflow-hidden mb-4': !devices.isMobile.value }"
        >
          <div
            v-if="layout === 'table'"
            class="single-table w-full"
            :class="{ 'overflow-scroll pb-4': devices.isMobile.value }"
          >
            <div
              class="p-4 bg-semantic-surface-default flex flex-col md:flex-row md:items-center gap-3 justify-center border border-b-0 border-semantic-outline-default rounded-tl-2xl rounded-tr-2xl"
            >
              <div
                v-if="!_isEmpty(tableTitle)"
                class="text-xl/[120%] md:text-2xl/[120%] font-semibold text-semantic-secondary-variant-light"
              >
                {{ tableTitle }}
              </div>

              <V2MoleculesTabs
                v-model="tabsRef"
                position="right"
                :tabs="getTabs"
              />
            </div>

            <V2MoleculesTable
              table-class="w-full rounded-b-2xl"
              variant="alternate"
              :headers="thead"
              :items="tbody"
            />
          </div>

          <div v-if="layout === 'two-table' && splitData" class="two-table">
            <div
              class="p-4 bg-semantic-surface-default flex flex-col md:flex-row md:items-center gap-3 justify-center border border-b-0 border-semantic-outline-default rounded-tl-2xl rounded-tr-2xl"
            >
              <div
                v-if="!_isEmpty(tableTitle)"
                class="text-xl/[120%] md:text-2xl/[120%] font-semibold text-semantic-secondary-variant-light bg-red-"
              >
                {{ tableTitle }}
              </div>

              <V2MoleculesTabs
                v-model="tabsRef"
                position="right"
                :tabs="getTabs"
              />
            </div>
            <div v-for="(tableKey, tableIndex) in splitData" :key="tableIndex">
              <V2MoleculesTable
                table-class="w-full !rounded-2xl"
                variant="alternate"
                :headers="theadTwoTableV2(tableKey)"
                :items="tbodyTwoTableV2(tableKey)"
              />
            </div>
          </div>
        </div>

        <div
          v-if="footer"
          class="text-semantic-surface-on-surface mt-1 text-xs text-center"
          v-html="footer"
        />
      </div>
    </div>
  </ClientOnly>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

defineOptions({
  name: 'V2OrganismHomeComparisonTableTabs'
})

const { devices } = deviceChecker()

const props = defineProps({
  layout: {
    type: String,
    default: 'table',
    validator: value => {
      return ['table', 'two-table'].includes(value)
    }
  },

  title: {
    type: String,
    default: ''
  },

  tableTitle: {
    type: String,
    default: ''
  },

  description: {
    type: String,
    default: ''
  },

  footer: {
    type: String,
    default: ''
  },

  costs: {
    type: Object,
    default: () => {
      return {
        '$1 Million Cover': [
          {
            age: '45 Years Old',
            male: {
              daily: '$1.32',
              monthly: '$40.35'
            },
            female: {
              daily: '$1.08',
              monthly: '$32.99'
            }
          }
        ]
      }
    }
  },

  tabs: {
    type: Array,
    default: () => ['Tab 1', 'Tab 2']
  },

  bgClass: {
    type: String,
    default: ''
  },

  wrapperClass: {
    type: String,
    default: ''
  }
})

const tabsRef = ref('$1 Million Cover')

const thead = computed(() => {
  const costs = props.costs
  const costsKeys = Object.keys(costs[tabsRef.value])

  return [{ label: '' }, ...costsKeys.map(key => ({ label: key }))]
})

const tbody = computed(() => {
  const costs = props.costs
  const costsKeys = Object.keys(costs[tabsRef.value])
  const dataKeys = Object.keys(costs[tabsRef.value][costsKeys[0]])
  const tableRows = []
  dataKeys.forEach(item => {
    const row = []
    row.push({
      value: transformText(item)
    })

    costsKeys.forEach(label => {
      const costValue = costs[tabsRef.value][label][item]
      row.push({
        value: costValue
      })
    })
    tableRows.push(row)
  })
  return tableRows
})

const getTabs = computed(() => {
  return props.tabs.map(key => ({ label: key, key }))
})

const transformText = value => {
  return '<span class="font-bold">' + value + '</span>'
}

const splitData = computed(() => {
  const data = props.costs[tabsRef.value]
  const upData = {}
  const bottomData = {}

  for (const key in data) {
    upData[key] = {}
    bottomData[key] = {}

    const dataGroups = data[key]
    const dataKeys = Object.keys(dataGroups)
    const middleIndex = Math.ceil(dataKeys.length / 2)

    dataKeys.forEach((_, index) => {
      if (index < middleIndex) {
        upData[key] = {
          'Male - Daily Premium': dataGroups['Male - Daily Premium'],
          'Male - Monthly Premium': dataGroups['Male - Monthly Premium']
        }
      } else {
        bottomData[key] = {
          'Female - Daily Premium': dataGroups['Female - Daily Premium'],
          'Female - Monthly Premium': dataGroups['Female - Monthly Premium']
        }
      }
    })
  }

  return { upData, bottomData }
})

const theadTwoTable = data => {
  const dataKey = Object.keys(data)[0]
  const theadKey = Object.keys(data[dataKey])

  return theadKey
}

const theadTwoTableV2 = data => {
  const dataKey = Object.keys(data)[0]
  const theadKey = Object.keys(data[dataKey])

  return [{ label: 'Age' }, ...theadKey.map(key => ({ label: key }))]
}
const tbodyTwoTableV2 = data => {
  const allRows = []

  const dataKey = Object.keys(data)

  dataKey.forEach(item => {
    const row = []

    row.push({
      value: transformText(item)
    })

    const innerDataKeys = Object.keys(data[item])

    innerDataKeys.forEach(label => {
      const costValue = data[item][label]
      row.push({
        value: costValue
      })
    })

    allRows.push(row)
  })

  return allRows
}
</script>

<style scoped>
.single-table table {
  @apply w-full;
}
.single-table table :deep(tbody) {
  @apply rounded-b-2xl w-full;
}
.two-table .two-table__container {
  @apply border border-gray-400 rounded-2xl mb-4;
}
.two-table table {
  @apply w-full;
}
.two-table table thead {
  @apply border-b border-b-gray-400;
}
.two-table table thead th {
  @apply py-[0.375rem] md:py-4 w-1/3 px-2;
}
.two-table table thead th:first-child {
  @apply bg-blue-5 rounded-tl-[0.9375rem];
}
.two-table table thead th:nth-child(2n) {
  @apply bg-blue-10;
}
.two-table table thead th:last-child {
  @apply bg-blue-20 rounded-tr-[0.9375rem];
}
.two-table table tbody tr {
  @apply even:bg-blue-5 odd:bg-white border-b border-b-gray-400;
}
.two-table table tbody tr:last-child {
  @apply border-b-0;
}
.two-table table tbody tr:last-child td:first-child {
  @apply rounded-bl-[0.9375rem];
}
.two-table table tbody tr:last-child td:last-child {
  @apply rounded-br-[0.9375rem];
}
.two-table table tbody tr td {
  @apply py-[0.625rem];
}
</style>
